import React, { useState } from 'react';

import { useSnackbar } from 'notistack';

import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Tooltip,
} from '@mui/material';

import { useExportLensPdf } from '~/hooks/apis/reporting/useExportLensPdf';

import { downloadLensCsv } from '~/utils/downloadLensCsv';

import type { LensState } from './types';

type LensDownloadProps = {
  lensId: string;
  lensVersion?: number;
  title: string;
  state: LensState;
  blocks: Array<string>;
  tabs?: Array<string>;
  /** Shows the menu open and inert (first-run tour demo). */
  forceOpen?: boolean;
};

export default function LensDownload({
  lensId,
  lensVersion,
  title,
  state,
  blocks,
  tabs,
  forceOpen,
}: LensDownloadProps) {
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const [csvPending, setCsvPending] = useState(false);
  const exportPdf = useExportLensPdf(lensId);
  const { enqueueSnackbar } = useSnackbar();
  const pending = exportPdf.isPending || csvPending;
  const pdf = () => {
    setOpen(false);
    exportPdf.mutate({ state, title, lensVersion });
  };
  const csv = () => {
    setOpen(false);
    setCsvPending(true);
    downloadLensCsv(lensId, { state, blocks, tabs, lensVersion })
      .catch(() => enqueueSnackbar('The CSV didn’t download. Try again.', { variant: 'warning' }))
      .finally(() => setCsvPending(false));
  };

  const menuItems = [
    <MenuItem data-menu-item="pdf" key="pdf" onClick={pdf}>
      <ListItemIcon>
        <PictureAsPdfOutlinedIcon fontSize="small" />
      </ListItemIcon>
      PDF
    </MenuItem>,
    blocks.length ? (
      <MenuItem data-menu-item="csv" key="csv" onClick={csv}>
        <ListItemIcon>
          <TableChartOutlinedIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="CSV" secondary={blocks.length > 1 ? `${blocks.length} blocks, zipped` : undefined} />
      </MenuItem>
    ) : null,
  ];

  return (
    <>
      <Tooltip
        title={
          pending ? 'Preparing the download…' : exportPdf.isError ? 'The PDF could not be made. Try again.' : 'Download'
        }
      >
        <span>
          <IconButton
            aria-haspopup="menu"
            aria-label="Download this Lens"
            disabled={pending}
            onClick={() => setOpen(true)}
            ref={setAnchor}
            size="small"
          >
            <FileDownloadOutlinedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      {forceOpen ? (
        <Popper
          anchorEl={anchor}
          disablePortal
          open
          placement="bottom-end"
          popperOptions={{ strategy: 'fixed' }}
          sx={{ zIndex: 30 }}
        >
          <Paper elevation={8} inert sx={{ pointerEvents: 'none' }}>
            <MenuList dense>{menuItems}</MenuList>
          </Paper>
        </Popper>
      ) : (
        <Menu anchorEl={anchor} onClose={() => setOpen(false)} open={open}>
          {menuItems}
        </Menu>
      )}
    </>
  );
}
